// Shared projects from a client's side (PROTOCOL.md §10): the account's exchange key (made on the next unlock when it
// has none, opened with the AVK, kept in memory only), the owner's invitations, confirmations (the project key wrapped
// to the member's exchange key once the safety code is compared), members and roles, and the member's side — accepting
// an invitation, opening the key (the member wrap, then their own AVK copy), and leaving. The relay's thin calls are in
// shares.ts; this file adds the keys.
import { fromB64u, toB64u } from '../shared/b64.ts';
import { fingerprint, openSummary, type ProjectSummary } from '../shared/crypto.ts';
import {
  makeExchangeKey, openExchangeKey, safetyCode, unwrapForMember, unwrapSharedKey, wrapForMember, wrapSharedKey,
  type ExchangeKey, type ExchangeKeyUpload,
} from '../shared/members.ts';
import { ApiError, type Api } from './api.ts';
import * as S from './shares.ts';

export type ShareRole = S.ShareRole;
export type ProjectRole = 'owner' | ShareRole;

/** The limits of §10: co-writers and commenters besides the owner. */
export const LIMITS = { cowriter: 3, commenter: 5 } as const;

// The exchange key ---------------------------------------------------------------------------------------------------

/**
 * The account's exchange key, opened with the AVK; made (and uploaded) when the account has none. Returns null when
 * the relay's copy won't open with this AVK (another vault's: never replaced from here).
 */
export async function ensureExchangeKey(api: Api, accountId: string, avk: Uint8Array): Promise<ExchangeKey | null> {
  let stored: ExchangeKeyUpload | null = null;
  try {
    stored = await S.getExchangeKey(api);
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 404)) throw e;
  }
  if (stored) {
    try {
      return await openExchangeKey(avk, accountId, stored);
    } catch {
      return null;
    }
  }
  const made = await makeExchangeKey(avk, accountId);
  await S.putExchangeKey(api, made.upload);
  return made.key;
}

// The owner's side ---------------------------------------------------------------------------------------------------

export interface PendingMember {
  account: string;
  name: string;
  role: ShareRole;
  publicKey: Uint8Array;
  /** What both people read to each other before the owner confirms. */
  safetyCode: string;
}

/** The safety code of the owner's exchange key and a member's public key. */
export const codeWith = (own: ExchangeKey, publicKey: string | Uint8Array) =>
  safetyCode(own.pub, typeof publicKey === 'string' ? fromB64u(publicKey) : publicKey);

/** Confirms a member: the project key wrapped to their exchange key, with the key's fingerprint. */
export async function confirmMember(api: Api, owner: string, projectId: string, member: { account: string; role: ShareRole; publicKey: string | Uint8Array }, projectKey: Uint8Array) {
  const pub = typeof member.publicKey === 'string' ? fromB64u(member.publicKey) : member.publicKey;
  const wrapped = await wrapForMember(pub, owner, projectId, member.account, projectKey);
  await S.confirmMember(api, projectId, member.account, { role: member.role, wrappedKey: toB64u(wrapped), keyFingerprint: await fingerprint(projectKey) });
}

// The member's side --------------------------------------------------------------------------------------------------

export interface OpenedShare {
  owner: string;
  ownerName: string;
  projectId: string;
  role: ShareRole;
  status: S.Share['status'];
  /** Null while pending, or when neither wrap opens. */
  key: Uint8Array | null;
  keyFingerprint: string | null;
  summary: ProjectSummary | null;
  /** The share as the relay listed it (kept for offline starts: only wrapped keys and sealed summaries). */
  raw: S.Share;
}

/**
 * Opens a share's key: the member's own AVK copy when stored, else the member wrap with the exchange key — then
 * stores the AVK copy (`PUT /api/shares/{owner}/{project}/key`) so later opens need no ECDH. A wrap for an older key
 * than the share's fingerprint is passed over for the other.
 */
export async function openShare(
  api: Api | null,
  accountId: string,
  avk: Uint8Array,
  exchange: () => Promise<ExchangeKey | null>,
  share: S.Share,
): Promise<OpenedShare> {
  const base = { owner: share.owner, ownerName: share.ownerName ?? '', projectId: share.projectId, role: share.role, status: share.status, raw: share };
  let key: Uint8Array | null = null;
  const fits = async (k: Uint8Array) => !share.keyFingerprint || (await fingerprint(k)) === share.keyFingerprint;
  if (share.avkWrap) {
    try {
      const k = await unwrapSharedKey(avk, accountId, share.owner, share.projectId, fromB64u(share.avkWrap));
      if (await fits(k)) key = k;
    } catch {
      // another vault's, or damaged: the member wrap
    }
  }
  if (!key && share.memberWrap) {
    const ex = await exchange();
    if (ex) {
      try {
        const k = await unwrapForMember(ex, share.owner, share.projectId, accountId, fromB64u(share.memberWrap));
        if (await fits(k)) {
          key = k;
          if (api) {
            const avkWrap = toB64u(await wrapSharedKey(avk, accountId, share.owner, share.projectId, k));
            await S.storeShareKey(api, share.owner, share.projectId, avkWrap, share.keyFingerprint ?? undefined).catch(() => {});
            share = { ...share, avkWrap };
          }
        }
      } catch {
        // not for this exchange key
      }
    }
  }
  const summary = key && share.summary ? await openSummary(key, fromB64u(share.summary)).catch(() => null) : null;
  return { ...base, raw: share, key, keyFingerprint: key ? await fingerprint(key) : null, summary };
}
